/** The demo's deterministic traffic: what each grouping splits into, and one group's numbers on one day. */

import { GA4_METRIC_KEYS, type Ga4GroupBy, type Ga4MetricKey, type Ga4Property, type Ga4TrafficMetrics, type Ga4TrafficReport } from '@/api/types'
import { unitNoise, wobble } from '@/mock/random'

type Weighted = ReadonlyArray<readonly [label: string, weight: number]>

/** What each grouping splits traffic into, and roughly how much each part carries. */
export const GROUPS: Record<Ga4GroupBy, Weighted> = {
  channel: [
    ['Organic Search', 0.31],
    ['Direct', 0.22],
    ['Paid Social', 0.19],
    ['Email', 0.1],
    ['Referral', 0.06],
    ['Organic Social', 0.06],
    ['Paid Search', 0.05],
    ['Unassigned', 0.01],
  ],
  source_medium: [
    ['google / organic', 0.3],
    ['(direct) / (none)', 0.22],
    ['facebook / paid_social', 0.13],
    ['instagram / paid_social', 0.06],
    ['mailchimp / email', 0.1],
    ['bing / organic', 0.04],
    ['moneysavingexpert.com / referral', 0.05],
    ['google / cpc', 0.05],
    ['(not set)', 0.05],
  ],
  campaign: [
    ['(not set)', 0.42],
    ['summer-escapes', 0.2],
    ['cash-drop-friday', 0.16],
    ['new-customer-welcome', 0.12],
    ['retargeting-basket', 0.1],
  ],
  landing: [
    ['/', 0.34],
    ['/competitions/', 0.24],
    ['/competitions/vw-campervan/', 0.14],
    ['/competitions/2000-cash/', 0.1],
    ['/winners/', 0.08],
    ['/basket/', 0.06],
    ['/my-account/', 0.04],
  ],
  device: [
    ['mobile', 0.63],
    ['desktop', 0.31],
    ['tablet', 0.06],
  ],
  country: [
    ['United Kingdom', 0.86],
    ['Ireland', 0.06],
    ['United States', 0.03],
    ['Germany', 0.02],
    ['France', 0.015],
    ['(not set)', 0.015],
  ],
}

/** Sessions a day across every group, before the weights split them; a demo property's whole personality. */
export const DEFAULT_BASE_SESSIONS = 420

/** `newUsers` → `new_users`: the API's CSV column names are the metric keys in snake case. */
const snakeKey = (key: string): string => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

/**
 * A `Ga4TrafficMetrics` with `value(key)` for every key of `GA4_METRIC_KEYS`.
 * The one cast is safe while the key list is complete, which
 * `Ga4MetricKey` checks at compile time.
 */
function fromKeys(value: (key: Ga4MetricKey) => number): Ga4TrafficMetrics {
  const built: Partial<Record<Ga4MetricKey, number>> = {}
  for (const key of GA4_METRIC_KEYS) built[key] = value(key)
  return built as Ga4TrafficMetrics
}

/** Every metric at zero, built from `GA4_METRIC_KEYS`: a metric added to the list is zero here with no edit. */
export const ZERO: Ga4TrafficMetrics = fromKeys(() => 0)

function seedOf(propertyId: string): number {
  return Number(propertyId.slice(-6)) || 1
}

/** One group's traffic on one day: a pure function of its inputs, never of call order. */
export function dayCell(property: Ga4Property, base: number, group: string, weight: number, date: string): Ga4TrafficMetrics {
  const seed = seedOf(property.propertyId)
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay()
  const weekend = weekday === 0 || weekday === 6 ? 1.12 : 0.96
  const sessions = Math.round(base * weight * weekend * wobble(seed, 0.28, group, date))
  if (sessions <= 0) return ZERO
  const conversions = Math.round(sessions * (0.01 + unitNoise(seed, 'rate', group) * 0.05) * wobble(seed, 0.3, 'c', group, date))
  const purchases = Math.round(conversions * 0.9)
  return {
    sessions,
    users: Math.round(sessions * 0.82 * wobble(seed, 0.05, 'u', group, date)),
    newUsers: Math.round(sessions * 0.36 * wobble(seed, 0.1, 'n', group, date)),
    engagedSessions: Math.round(sessions * 0.6 * wobble(seed, 0.08, 'e', group, date)),
    conversions,
    purchases,
    revenueMinor: Math.round(purchases * 1850 * wobble(seed, 0.2, 'r', group, date)),
  }
}

export function addMetrics(a: Ga4TrafficMetrics, b: Ga4TrafficMetrics): Ga4TrafficMetrics {
  return fromKeys((key) => (a[key] ?? 0) + (b[key] ?? 0))
}

/** The demo's CSV file: the API's columns (key, label, each metric, currency), one quoted row per group. */
export function trafficCsv(report: Ga4TrafficReport): Blob {
  const header = ['key', 'label', ...GA4_METRIC_KEYS.map(snakeKey), 'currency']
  const lines = report.rows.map((row) =>
    [row.key, row.label, ...GA4_METRIC_KEYS.map((key) => row[key] ?? ''), report.currency ?? '']
      .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
      .join(','),
  )
  return new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' })
}
